import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Barcode as BarcodeIcon,
  CalendarClock,
  MapPin,
  Package,
  ShoppingCart,
  Sparkles,
} from 'lucide-react';
import Header from '../components/Header';
import BackButton from '../components/BackButton';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import useShopTools from '../hooks/useShopTools';

// Ordered list of tools with metadata used by the switch cards.
const TOOL_DEFS = [
  {
    key: 'inventario',
    label: 'Inventario',
    icon: Package,
    description: 'Gestione quantità in magazzino, movimenti, soglia scorta e caricamento prodotti.',
    deactivateWarning:
      'Disattivando lo strumento Inventario, le quantità dei tuoi prodotti verranno convertite nella modalità "Presente / Esaurito". I valori numerici andranno persi. Vuoi continuare?',
  },
  {
    key: 'cassa',
    label: 'Cassa',
    icon: ShoppingCart,
    description: 'POS mobile: se anche Inventario è attivo scarica le quantità; se Inventario è OFF funziona come conteggio rapido senza decremento.',
    deactivateWarning:
      'Disattivando la Cassa non potrai più registrare vendite dallo strumento POS. Nessun dato prodotto verrà eliminato. Vuoi continuare?',
  },
  {
    key: 'barcode',
    label: 'Sistema Barcode',
    icon: BarcodeIcon,
    description: 'Scansione codici a barre per ricerca veloce e associazione ai prodotti.',
    deactivateWarning:
      'Disattivando il Barcode i codici già associati ai prodotti restano salvati, ma non potrai più cercarli via scansione. Vuoi continuare?',
  },
  {
    key: 'eventi',
    label: 'Eventi',
    icon: CalendarClock,
    description: 'Crea trunk show, workshop e pop-up con data, orario e sede dedicata.',
    deactivateWarning:
      'Disattivando gli Eventi la pagina eventi e tutti i menu correlati non saranno più visibili. Gli eventi già creati NON verranno eliminati, ma resteranno nascosti finché non riattivi lo strumento. Vuoi continuare?',
  },
  {
    key: 'servizi',
    label: 'Servizi',
    icon: Sparkles,
    description: 'Offri appuntamenti e servizi (es. sartoria, personal shopper) accanto ai prodotti.',
    deactivateWarning:
      'Disattivando i Servizi non potrai più crearne di nuovi. I servizi già pubblicati NON verranno eliminati, ma resteranno nascosti finché non riattivi lo strumento. Vuoi continuare?',
  },
  {
    key: 'posizione_temporanea',
    label: 'Posizione temporanea',
    icon: MapPin,
    description: "Aggiungi al negozio sedi temporanee (mercatini, fiere) con date d'inizio e fine.",
    deactivateWarning:
      'Disattivando la Posizione temporanea le eventuali sedi extra non saranno più visibili ai clienti. I dati saranno conservati e torneranno visibili alla riattivazione. Vuoi continuare?',
  },
];

export default function StrumentiPage() {
  const navigate = useNavigate();
  const { tools, loading, isEnabled, update } = useShopTools();
  const [saving, setSaving] = useState(null);      // key currently being updated
  const [pendingOff, setPendingOff] = useState(null); // tool waiting for confirmation

  const applyChange = async (key, value) => {
    setSaving(key);
    try {
      const patch = { [key]: value };
      // v1.8.1 cascade rules per spec:
      // - Inventario e Cassa si attivano e disattivano SINGOLARMENTE.
      // - Se Inventario viene ATTIVATO → attiva anche Cassa (comodità).
      // - Inventario OFF + Cassa ON = modalità "conteggio rapido" senza
      //   decremento (gestita lato backend); nessuna cascata forzata.
      if (key === 'inventario' && value && !isEnabled('cassa')) {
        patch.cassa = true;
      }
      await update(patch);
      toast.success(`${TOOL_DEFS.find((t) => t.key === key).label} ${value ? 'attivato' : 'disattivato'}`);
    } catch (e) {
      toast.error("Impossibile aggiornare lo strumento. Riprova.");
    } finally {
      setSaving(null);
    }
  };

  const onToggle = (def, next) => {
    // Deactivation always requires an explicit confirmation dialog.
    if (isEnabled(def.key) && !next) {
      setPendingOff(def);
      return;
    }
    applyChange(def.key, next);
  };

  const confirmDeactivation = async () => {
    if (!pendingOff) return;
    const key = pendingOff.key;
    setPendingOff(null);
    await applyChange(key, false);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="strumenti-page">
      <Header showSearch={false} />
      <main className="max-w-3xl mx-auto px-4 py-4">
        <BackButton fallback="/dashboard" className="mb-3" />
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-slate-800 mb-1">
          Aggiungi strumenti
        </h1>
        <p className="text-slate-600 text-sm mb-6">
          Attiva solo le funzionalità che ti servono. Puoi cambiarle in qualsiasi momento.
        </p>

        {loading ? (
          <p className="text-slate-500">Caricamento…</p>
        ) : (
          <div className="space-y-3">
            {TOOL_DEFS.map((def) => {
              const Icon = def.icon;
              const enabled = isEnabled(def.key);
              const isSaving = saving === def.key;
              return (
                <div
                  key={def.key}
                  className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start gap-4"
                  data-testid={`tool-card-${def.key}`}
                >
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold text-slate-900">{def.label}</h3>
                      <Switch
                        checked={enabled}
                        disabled={isSaving}
                        onCheckedChange={(v) => onToggle(def, v)}
                        data-testid={`tool-toggle-${def.key}`}
                      />
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{def.description}</p>
                    {def.requires && !isEnabled(def.requires) && enabled && (
                      <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1 mt-2 inline-block">
                        Richiede lo strumento &quot;{def.requires}&quot;.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <AlertDialog open={!!pendingOff} onOpenChange={(o) => !o && setPendingOff(null)}>
        <AlertDialogContent data-testid="deactivate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disattivare &quot;{pendingOff?.label}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingOff?.deactivateWarning}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeactivation}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
              data-testid="deactivate-confirm"
            >
              Disattiva {pendingOff?.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
